import { useSignals } from "@/hooks/useNews";
import { type NormalizedSignal } from "@/lib/types";
import { AlertTriangle, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

interface ToastItem {
  signal: NormalizedSignal;
  id: string;
  expiresAt: number;
}

const SEVERITY_COLOR: Record<string, string> = {
  critical: "#ff4444",
  high: "#ff9100",
};

const AUTO_DISMISS_MS = 8000;

export default function SignalToast() {
  const { data } = useSignals();
  const seenIds = useRef<Set<string>>(new Set());
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const initialized = useRef(false);

  useEffect(() => {
    if (!data?.signals) return;

    const alertable = data.signals.filter(
      (s) => (s.severity === "critical" || s.severity === "high") && s.status === "live",
    );

    if (!initialized.current) {
      // On first load, seed seen IDs without toasting — only alert on genuinely new arrivals
      alertable.forEach((s) => seenIds.current.add(s.id));
      initialized.current = true;
      return;
    }

    const fresh = alertable.filter((s) => !seenIds.current.has(s.id));
    if (!fresh.length) return;

    fresh.forEach((s) => seenIds.current.add(s.id));

    const now = Date.now();
    setToasts((prev) => [
      ...prev,
      // At most show 3 at once — newest entries first
      ...fresh.slice(0, 3 - prev.length).map((s) => ({
        signal: s,
        id: `${s.id}-${now}`,
        expiresAt: now + AUTO_DISMISS_MS,
      })),
    ]);
  }, [data]);

  // Auto-dismiss loop
  useEffect(() => {
    if (!toasts.length) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setToasts((prev) => prev.filter((t) => t.expiresAt > now));
    }, 500);
    return () => clearInterval(timer);
  }, [toasts.length]);

  function dismiss(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  if (!toasts.length) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 320,
        pointerEvents: "none",
      }}
    >
      {toasts.map((toast) => {
        const color = SEVERITY_COLOR[toast.signal.severity] ?? "#ff9100";
        const progress = Math.max(0, (toast.expiresAt - Date.now()) / AUTO_DISMISS_MS);
        return (
          <div
            key={toast.id}
            style={{
              background: "#0d0d0d",
              border: `1px solid ${color}44`,
              borderLeft: `3px solid ${color}`,
              padding: "12px 14px",
              boxShadow: `0 4px 24px ${color}22`,
              pointerEvents: "auto",
              position: "relative",
              overflow: "hidden",
              animation: "tsm-toast-in 0.2s ease-out",
            }}
          >
            {/* Progress bar */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                height: 2,
                width: `${progress * 100}%`,
                background: color,
                opacity: 0.5,
                transition: "width 0.5s linear",
              }}
            />

            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <AlertTriangle size={13} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: "0.1em",
                      color,
                      border: `1px solid ${color}`,
                      padding: "1px 5px",
                      textTransform: "uppercase",
                    }}
                  >
                    {toast.signal.severity}
                  </span>
                  <span style={{ color: "#555", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {toast.signal.source} · {toast.signal.category}
                  </span>
                </div>
                <p
                  style={{
                    color: "#f0f0f0",
                    fontSize: 12,
                    fontWeight: 600,
                    margin: 0,
                    lineHeight: 1.35,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {toast.signal.title}
                </p>
                {toast.signal.location && (
                  <p style={{ color: "#555", fontSize: 10, margin: "4px 0 0" }}>{toast.signal.location}</p>
                )}
              </div>
              <button
                onClick={() => dismiss(toast.id)}
                style={{ background: "none", border: "none", color: "#444", cursor: "pointer", padding: 0, flexShrink: 0 }}
              >
                <X size={12} />
              </button>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes tsm-toast-in {
          from { opacity: 0; transform: translateX(16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
